import { SurveyAnswers, SurveyInstance, SurveyModuleInfo, SurveyTemplate } from "@allaihealth/survey";
import { PendingChanges } from "./module-loader";

/** Manager that keeps track of the survey state */
export class SurveyState {
	/** The current instance of the survey */
	private _surveyInstance:SurveyInstance;

	/** Keep track of the modules */
	private modules:Map<string, ModuleStorage> = new Map<string, ModuleStorage>();

	constructor(initial:SurveyInstance) {
		this.surveyInstance = initial;
	}

	/** Has the given module already been loaded? */
	hasModule(moduleId:string) {
		return this.modules.has(moduleId);
	}

	/** Update the state with new answers and the current page number */
	update(answers:SurveyAnswers, pageNumber:number = 0) {
		this.surveyInstance.pageNo = pageNumber;
		this.surveyInstance.answers = answers;
		if(!this.surveyInstance.prunedAnswers) {
			this.surveyInstance.prunedAnswers = {}
		}

		for(const question in answers) {
			this.surveyInstance.prunedAnswers[question] = answers[question];
		}
	}

	/** Reload the modules in the map */
	private refreshModules() {
		this.modules.clear();

		for(let i = 0; i < this.surveyInstance.modules.length; i++) {
			const module = this.surveyInstance.modules[i];
			this.modules.set(module.moduleId, {...module, index: i});
		}
	}

	/**
	 * Add a new module
	 * @param moduleId the id of the module being added
	 * @param newModule the survey json being added
	 */
	addModule(moduleId:string, newModule:SurveyTemplate) {
		this.surveyInstance.modules.push({
			moduleId,
			survey: newModule
		});

		this.refreshModules();
	}

	/** Merge and build the modules into one survey, this will also rebuild the answers */
	buildSurveyJson() {
		const survey:SurveyTemplate = {
			calculatedValues: [],
			triggers: [],
			...structuredClone(this.surveyInstance.modules[0].survey)
		};

		//original calculated values that shouldn't be overwritten
		const orgCalculatedValues:Set<string> =
			new Set<string>(survey.calculatedValues.map(value => value.name));

		for(let i = 1; i < this.surveyInstance.modules.length; i++) {
			const module = this.surveyInstance.modules[i];

			survey.pages.push(...module.survey.pages);

			if(module.survey.triggers) {
				for(const trigger of module.survey.triggers) {
					survey.triggers.push({
						sourceId: module.moduleId,
						...trigger
					} as any);
				}
			}

			if(module.survey.calculatedValues) {
				for(const calculatedValue of module.survey.calculatedValues) {
					if(!orgCalculatedValues.has(calculatedValue.name)) {
						survey.calculatedValues.push(calculatedValue);
					}
				}
			}
		}

		for(const page of survey.pages) {
			if(!!page.elements) {
				for(const element of page.elements) {
					if(!this.surveyInstance.answers[element.name] && this.surveyInstance.prunedAnswers[element.name]) {
						this.surveyInstance.answers[element.name] = this.surveyInstance.prunedAnswers[element.name];
					}
				}
			}
		}

		return survey;
	}

	/**
	 * Set a survey variable in the instance
	 * @param variable the variable's name
	 * @param value the value of the variable to set
	 */
	setSurveyVariable(variable:string, value:any) {
		const firstModule = this.surveyInstance.modules[0].survey;
		const values = firstModule.calculatedValues.filter(value => value.name == variable);

		if(values?.length) {
			values[0].expression = value;
		} else {
			firstModule.calculatedValues.push(
				{
					name: variable,
					expression: value
				}
			)
		}
	}

	/**
	 * Parse the given changes and alter the loaded modules
	 * @param pending the pending changes to make
	 * @return true if the survey instance was altered
	 */
	parseChanges(pending:PendingChanges) {
		let wasChanged = false;

		if(pending.toUnload.size > 0) {
			let unloadIndex:number = -1;

			//go through each module to unload and find the earliest module to unload
			for(const unload of pending.toUnload) {
				const module = this.modules.get(unload);
				if(module && (unloadIndex < 0 || module.index < unloadIndex)) {
					unloadIndex = module.index;
				}
			}

			//if an unload module was found, unload it and subsequent modules
			if(unloadIndex >= 0) {
				const deleted = this.surveyInstance.modules.splice(unloadIndex);
				console.info(`Unloaded modules ${deleted.map(value => value.moduleId).join(", ")}`);
				this.refreshModules();
				wasChanged = true;
			}
		}

		if(pending.toLoad.length > 0) {
			//go through each module to load and add it in
			for(const load of pending.toLoad) {
				if(!this.hasModule(load.moduleId) && (this.hasModule(load.sourceId) || load.sourceId == undefined)) {
					console.info(`Adding module ${load.moduleId}`);
					this.addModule(load.moduleId, load.survey);
					wasChanged = true;
					break;
				}
			}
		}

		return wasChanged;
	}

	/** Get the state's current answers */
	get answers() {
		return this.surveyInstance.answers;
	}

	/** Set the current internal instance */
	set surveyInstance(value:SurveyInstance) {
		this._surveyInstance = value;
		this.refreshModules();
	}

	/** Get the current internal instance */
	get surveyInstance():SurveyInstance {
		return this._surveyInstance;
	}
}

/** Special interface for storing modules */
interface ModuleStorage extends SurveyModuleInfo {
	/** The index in the module array (for easy removal) */
	index:number;
}
